// The monitor. Everything above this file draws a desktop; this file puts a computer around it —
// a warm beige CRT shell with a recessed screen, a chin carrying the globe and a power LED, and a
// glass layer of glare, vignette and scanlines over the whole thing.
// Lifted in spirit from y2kdotcom.xyz (see research/Y2K-DOTCOM-TEARDOWN.md — the single best idea on
// their site) and rebuilt in ours: their shell is an iMac G3 in cream, ours is a beige PC monitor
// with a lime power LED, because we are a Windows desktop, not a Mac one.
// Double-click the chin to take the monitor away; a small tab in the corner brings it back.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import globe from 'assets/pfp.png';
import sfx from 'lib/sfx';
import { FRAME, MIN_WIDTH, bezelOn, subscribe, toggleBezel } from './inset';

function Bezel({ children }) {
  const [state, setState] = useState(read);
  useEffect(() => subscribe(() => setState(read())), []);

  // The sound layer mounts here because Bezel is the one wrapper that is always around the whole
  // desktop, so a single delegated listener covers every control on the site. App.js is the more
  // natural home and this should move there when that file is free.
  useEffect(() => sfx.attach(), []);

  const { on, wide } = state;

  // ONE tree in both states. The desktop stays mounted at the same spot whether the monitor is
  // drawn or not; switching wrappers here remounted <WinXP/> and threw removeChild errors from
  // webamp's DOM on every phone-breakpoint crossing (found by the red-team pass, 2026-09-11).
  return (
    <Shell className={on ? 'bezel--on' : 'bezel--off'}>
      <div className="bezel__screen">
        <div className="bezel__crt">{children}</div>
        <div className="bezel__glass" />
      </div>
      <div
        className="bezel__chin"
        onDoubleClick={() => {
          const next = !bezelOn();
          toggleBezel();
          if (next) sfx.degauss();
        }}
        title="double-click to take the monitor away"
      >
        <img src={globe} alt="" />
        <span className="bezel__brand">INTERNET COIN<sup>™</sup></span>
        <span className="bezel__vent" />
        <span
          className="bezel__led"
          title="power"
          onClick={() => {
            const next = !bezelOn();
            toggleBezel();
            if (next) sfx.degauss();
          }}
        />
      </div>
      {!on && wide ? (
        <button type="button" className="bezel__restore" title="Put the monitor back" onClick={toggleBezel}>
          ▣
        </button>
      ) : null}
    </Shell>
  );
}

// on = the monitor is drawn; wide = the viewport could draw it (phones never do, so they get no restore tab)
function read() {
  return { on: bezelOn(), wide: typeof window !== 'undefined' && window.innerWidth >= MIN_WIDTH };
}

const Shell = styled.div`
  height: 100%;
  padding: ${FRAME.top}px ${FRAME.x}px ${FRAME.bottom}px;
  box-sizing: border-box;
  position: relative;
  /* the monitor taken away, or a phone: same tree, no frame */
  &.bezel--off {
    padding: 0;
    background: none;
    box-shadow: none;
  }
  &.bezel--off::before,
  &.bezel--off .bezel__glass,
  &.bezel--off .bezel__chin {
    display: none;
  }
  &.bezel--off .bezel__screen {
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }
  &.bezel--off .bezel__crt {
    inset: 0;
    border-radius: 0;
  }
  .bezel__restore {
    position: fixed;
    right: 6px;
    bottom: 34px;
    z-index: 99997;
    width: 22px;
    height: 22px;
    padding: 0;
    line-height: 1;
    font-size: 13px;
    color: #2f3b00;
    background: linear-gradient(#f6ffd0, #dcf58a);
    border: 1px solid #7a9a00;
    border-radius: 3px;
    cursor: pointer;
    opacity: 0.55;
    &:hover {
      opacity: 1;
    }
  }
  /* The plastic. Real beige-box moulding is warm, slightly uneven, and lit from above-left: a flat
     gradient reads as a coloured rectangle instead of an object. Three things do the work —
     a vertical tone shift, a very fine speckle for the injection-moulded grain, and hard 1px
     highlight/shadow lines where the faces meet. */
  background:
    radial-gradient(140% 60% at 22% -6%, rgba(255, 255, 255, 0.55), transparent 58%),
    linear-gradient(168deg, #efeada 0%, #ded8c2 30%, #c9c2a8 62%, #ada68c 86%, #948d76 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 1px 0 0 rgba(255, 255, 255, 0.45),
    inset -1px 0 0 rgba(0, 0, 0, 0.16),
    inset 0 -2px 0 rgba(0, 0, 0, 0.22),
    inset 0 -14px 22px rgba(0, 0, 0, 0.18),
    0 0 0 1px rgba(0, 0, 0, 0.42);
  /* the shell is the page background too, so the corners read as a physical object */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.5;
    /* moulded-plastic grain: two offset dot fields, far too fine to see as a pattern */
    background-image: radial-gradient(rgba(0, 0, 0, 0.16) 0.5px, transparent 0.5px),
      radial-gradient(rgba(255, 255, 255, 0.2) 0.5px, transparent 0.5px);
    background-size: 3px 3px, 3px 3px;
    background-position: 0 0, 1.5px 1.5px;
  }
  /* The well. A real monitor has a deep recess with a lip that catches light at the top and goes
     black at the bottom; that step is most of why a frame reads as physical. */
  .bezel__screen {
    position: relative;
    height: 100%;
    border-radius: 10px;
    background: #04060a;
    box-shadow:
      /* the moulded lip around the glass */
      inset 0 0 0 1px rgba(255, 255, 255, 0.32),
      inset 0 0 0 3px #6b6553,
      inset 0 0 0 4px rgba(0, 0, 0, 0.55),
      inset 0 0 0 7px #23221d,
      /* the recess itself */
      inset 0 8px 22px rgba(0, 0, 0, 0.9),
      inset 0 -4px 14px rgba(0, 0, 0, 0.7),
      /* the shadow the frame casts onto the plastic around it */
      0 2px 3px rgba(255, 255, 255, 0.5),
      0 0 26px rgba(0, 0, 0, 0.62);
    overflow: hidden;
  }
  .bezel__crt {
    position: absolute;
    inset: 5px;
    border-radius: 4px;
    overflow: hidden;
  }
  /* Glass: glare and a corner vignette only.
     The scanline layer that used to live here was a repeating 1px-in-3px black band at 16%.
     Over 11px Tahoma that is not a texture, it is a comb sitting on every glyph — Kris reported
     the screen as hard to read and this was why. A CRT reads as a CRT because of the FRAME, the
     recess and the glare; the lines were never doing the work people think they do, and they cost
     legibility on every single pixel of content. Gone, deliberately, not softened. */
  .bezel__glass {
    position: absolute;
    inset: 5px;
    z-index: 99996;
    pointer-events: none;
    border-radius: 4px;
    background:
      /* the diagonal sheet-glare across the top-left, the way a real tube catches a window */
      linear-gradient(
        102deg,
        rgba(255, 255, 255, 0.10) 0%,
        rgba(255, 255, 255, 0.03) 18%,
        rgba(255, 255, 255, 0) 34%,
        rgba(255, 255, 255, 0) 78%,
        rgba(255, 255, 255, 0.04) 100%
      ),
      /* the faint warm bloom a phosphor tube throws into its own corners */
      radial-gradient(120% 80% at 50% 0%, rgba(255, 255, 255, 0.05), transparent 60%);
    /* the vignette is what sells curvature, and it only touches the edges — not the text */
    box-shadow: inset 0 0 90px rgba(0, 0, 0, 0.42), inset 0 0 26px rgba(0, 0, 0, 0.34);
  }
  .bezel__chin {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${FRAME.bottom}px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 18px;
    cursor: default;
    user-select: none;
    img {
      width: 22px;
      height: 22px;
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.4));
    }
  }
  /* Engraved, not printed: a dark face with a 1px light edge below it is how stamped plastic
     lettering catches an overhead light. */
  .bezel__brand {
    font: italic 700 14px/1 'Trebuchet MS', Tahoma, sans-serif;
    letter-spacing: 1.5px;
    color: #7a745f;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.9), 0 -1px 0 rgba(0, 0, 0, 0.18);
    sup {
      font-size: 8px;
      letter-spacing: 0;
      color: #9a927a;
    }
  }
  /* Moulded vent slots — each slot is a dark groove with its own top highlight, rather than a
     flat striped band. */
  .bezel__vent {
    flex: 1;
    height: 9px;
    margin: 0 16px;
    border-radius: 5px;
    background:
      repeating-linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.22) 0 1.5px,
        rgba(255, 255, 255, 0.5) 1.5px 2.5px,
        transparent 2.5px 7px
      );
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.55);
    opacity: 0.75;
  }
  /* The LED sits in a moulded socket: a dark ring, the emitter, and the glow it throws onto the
     plastic around it. */
  .bezel__led {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #f4ffc0, #ccff00 45%, #86a800 100%);
    cursor: pointer;
    box-shadow:
      0 0 0 1.5px rgba(0, 0, 0, 0.35),
      0 0 0 2.5px rgba(255, 255, 255, 0.4),
      0 0 10px 3px rgba(204, 255, 0, 0.7),
      inset 0 -1px 2px rgba(0, 0, 0, 0.45);
    animation: bezel-pulse 3.2s ease-in-out infinite;
  }
  @keyframes bezel-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }
  @media (prefers-reduced-motion: reduce) {
    .bezel__led { animation: none; }
  }
  @media (max-width: ${MIN_WIDTH - 1}px) {
    padding: 0;
    background: #05070a;
    .bezel__screen { border-radius: 0; box-shadow: none; }
    .bezel__crt { inset: 0; border-radius: 0; }
    .bezel__glass, .bezel__chin, &::before { display: none; }
  }
`;

export default Bezel;
